import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { $apiV1, $apiV2 } from "../../config/api";
import { openVideoList } from "../../helpers/service";
import {
  buildSequenceEventOptionsFromApiMarkers,
  buildSequenceSearchPayload,
  createSequenceStepFromEventOption,
} from "./sequenceSearchUtils";
import "./playerRecruitmentSequences.css";

const MAX_SEQUENCES = 5;
const COMPETITION_PAGE_SIZE = 100;
const SEARCH_DELAY_MS = 250;
const LOAD_MORE_SCROLL_OFFSET = 120;
export const PLAYER_RECRUITMENT_TEMPLATE_PAGE_KEY =
  "player_recruitment_sequences";
const EXAMPLES = [
  { name: "High Press 2026", labels: ["Recovery", "Duel Won", "Pass+", "Shots on Target"] },
  { name: "Wide Overloads", labels: ["Long Ball+", "Dribbling Made+", "Cross+", "Shots on Target"] },
  { name: "Direct Attacking Transitions", labels: ["Interception", "Through Ball+", "Shot"] },
];
const idOf = (item) => item?.id ?? item?.competition_id ?? item?.player_id ?? item?.team_id ?? item?.match_id;
const seasonIdOf = (item) => item?.season_id ?? item?.year_season_id ?? item?.id;
const competitionIdOf = (item) => item?.competition_id ?? item?.competition__id ?? (typeof item?.competition === "object" ? item.competition?.id : item?.competition) ?? item?.id;
const labelOf = (item) => item?.display_name || item?.name || item?.title || item?.years || item?.year_season || `#${idOf(item)}`;
const teamLabelOf = (team) => typeof team === "string" ? team : team?.display_name || team?.name || team?.title || team?.team_name || "";
const compactMatchDate = (value) => {
  if (!value) return "";
  const isoParts = String(value).match(/^\d{4}-(\d{2})-(\d{2})/);
  if (isoParts) return `${isoParts[2]}.${isoParts[1]}`;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}` : "";
};
const matchLabelOf = (match, selectedTeams = []) => {
  const homeTeam = match?.home_team ?? match?.home_squad?.team ?? match?.home_team_name;
  const awayTeam = match?.away_team ?? match?.away_squad?.team ?? match?.away_team_name;
  const homeLabel = teamLabelOf(homeTeam);
  const awayLabel = teamLabelOf(awayTeam);
  const selected = selectedTeams[0];
  const selectedId = String(idOf(selected) ?? "");
  const selectedLabel = teamLabelOf(selected).toLowerCase();
  const homeId = String(idOf(homeTeam) ?? match?.home_team_id ?? match?.home_squad?.team_id ?? "");
  const awayId = String(idOf(awayTeam) ?? match?.away_team_id ?? match?.away_squad?.team_id ?? "");
  const selectedIsHome = Boolean(selected && ((selectedId && selectedId === homeId) || (selectedLabel && selectedLabel === homeLabel.toLowerCase())));
  const selectedIsAway = Boolean(selected && ((selectedId && selectedId === awayId) || (selectedLabel && selectedLabel === awayLabel.toLowerCase())));
  const opponentLabel = selectedIsHome ? `vs ${awayLabel}` : selectedIsAway ? `@ ${homeLabel}` : [homeLabel, awayLabel].filter(Boolean).join(" vs ");
  return [opponentLabel || `#${idOf(match)}`, compactMatchDate(match?.date ?? match?.match_date)].filter(Boolean).join(" ");
};
const unwrap = (response) => Array.isArray(response?.data?.results) ? response.data.results : Array.isArray(response?.data) ? response.data : [];
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const uniqueByLabel = (items) => {
  const byLabel = new Map();
  items.forEach((item) => {
    const key = String(labelOf(item)).trim().replace(/\s+/g, " ").toLowerCase();
    if (key && !byLabel.has(key)) byLabel.set(key, item);
  });
  return Array.from(byLabel.values());
};
const uniqueById = (items) => Array.from(new Map(items.map((item) => [String(idOf(item)), item])).values());

const toPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

const toObjectList = (value) =>
  Array.isArray(value) ? value.filter(toPlainObject) : [];

export const buildPlayerRecruitmentTemplateFilters = ({ scope, sequences } = {}) => ({
  version: 1,
  scope: {
    competition: toObjectList(scope?.competition),
    season: toObjectList(scope?.season),
    team: toObjectList(scope?.team),
    player: toObjectList(scope?.player),
    matches: toObjectList(scope?.matches),
  },
  sequences: (Array.isArray(sequences) ? sequences : [])
    .filter(toPlainObject)
    .map((sequence) => ({
      name: String(sequence.name || "Sequence"),
      included: sequence.included === true,
      steps: toObjectList(sequence.steps).map(({ id, ...step }) => ({ ...step })),
    })),
});

export const hasPlayerRecruitmentTemplateContent = (filters) => {
  const source = toPlainObject(filters) || {};
  const scope = toPlainObject(source.scope) || {};

  return Boolean(
    toObjectList(source.sequences).length ||
      ["competition", "season", "team", "player", "matches"].some(
        (key) => toObjectList(scope[key]).length
      )
  );
};

const parsePlayerRecruitmentTemplateFilters = (filters) => {
  const source = toPlainObject(filters) || {};
  const templateScope = toPlainObject(source.scope) || source;

  return {
    scope: {
      competition: toObjectList(templateScope.competition),
      season: toObjectList(templateScope.season),
      team: toObjectList(templateScope.team),
      player: toObjectList(templateScope.player),
      matches: toObjectList(templateScope.matches),
    },
    sequences: toObjectList(source.sequences).map((sequence, index) => ({
      id: uid(),
      name: String(sequence.name || `Sequence ${index + 1}`),
      included: sequence.included === true,
      editing: false,
      steps: toObjectList(sequence.steps).map((step) => ({ ...step, id: uid() })),
    })),
  };
};

// Options are filtered by label here unless the caller searches them itself (onQueryChange).
const MultiSelect = ({ label, required, options, selected, onChange, placeholder, disabled, getId = idOf, getLabel = labelOf, onQueryChange, onLoadMore, loading = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedIds = new Set(selected.map((item) => String(getId(item))));
  const summary = selected.length === 0 ? placeholder : selected.length === 1 ? getLabel(selected[0]) : `${getLabel(selected[0])} +${selected.length - 1}`;
  const filteredOptions = onQueryChange ? options : options.filter((option) => getLabel(option).toLowerCase().includes(query.trim().toLowerCase()));
  const changeQuery = (value) => {
    setQuery(value);
    onQueryChange?.(value);
  };
  const close = () => {
    setOpen(false);
    changeQuery("");
  };
  const toggle = () => (open ? close() : setOpen(true));
  const loadMoreNearEnd = (event) => {
    const menu = event.currentTarget;
    if (menu.scrollHeight - menu.scrollTop - menu.clientHeight <= LOAD_MORE_SCROLL_OFFSET) onLoadMore();
  };
  return (
    <div className="prs-field">
      <label>{label} {required && <span>required</span>}</label>
      <div className={`prs-ms ${open ? "open" : ""}`}>
        <button type="button" className={`prs-ms-btn ${selected.length ? "" : "placeholder"}`} disabled={disabled} onClick={toggle}>{summary}</button>
        {open && !disabled && <div className="prs-ms-menu" onScroll={onLoadMore ? loadMoreNearEnd : undefined}>
          <div className="prs-ms-search"><input autoFocus value={query} onChange={(event) => changeQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Escape") close(); }} placeholder={`Search ${label.toLowerCase()}...`} /></div>
          {filteredOptions.map((option) => {
            const key = String(getId(option));
            return <label className="prs-ms-opt" key={key}>
              <input type="checkbox" checked={selectedIds.has(key)} onChange={() => {
                onChange(selectedIds.has(key) ? selected.filter((item) => String(getId(item)) !== key) : [...selected, option]);
                close();
              }} />
              {getLabel(option)}
            </label>;
          })}
          {loading ? <div className="prs-picker-none">Loading…</div> : !filteredOptions.length && <div className="prs-picker-none">No results for “{query}”</div>}
        </div>}
      </div>
    </div>
  );
};

const RecruitmentSequenceResults = ({ sequenceName, eventTypes, expanded, setExpanded }) => (
  <section className={sequenceName ? "prs-result-sequence" : "prs-result-total"}>
    {sequenceName && <div className="prs-result-sequence-head">
      <div><small>Sequence</small><h3>{sequenceName}</h3></div>
      <span>{eventTypes.length} event type{eventTypes.length === 1 ? "" : "s"}</span>
    </div>}
    {eventTypes.map((eventType) => (
      <div className="prs-evsection" key={`${sequenceName}:${eventType.event_type}`}>
        <div className="prs-evhead">▼ {eventType.event_type}<span>top 5</span></div>
        <table className="prs-table"><thead><tr><th>Rank</th><th>Player</th><th>Occurrences</th><th></th></tr></thead><tbody>
          {eventType.players.map((player, index) => {
            const key = `${sequenceName}:${eventType.event_type}:${player.id}`;
            const playerMarkerIds = Array.from(new Set((player.sequences || []).flatMap((occurrence) => occurrence.marker_ids || []).map(String)));
            return <React.Fragment key={key}>
              <tr className="prs-playerrow" onClick={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))}><td>{index + 1}</td><td><b>{player.display_name}</b></td><td>{player.occurrences}</td><td>{expanded[key] ? "▼" : "▶"}</td></tr>
              {expanded[key] && <tr><td colSpan="4"><div className="prs-seqlist"><div className="prs-player-all"><span>All {player.occurrences} matching sequences</span><button onClick={() => openVideoList(playerMarkerIds.join(","), `${player.display_name} - ${eventType.event_type}`, false, playerMarkerIds, true, true)}>▶ Play all</button></div>{player.sequences.map((occurrence, occurrenceIndex) => <div className="prs-seqline" key={`${key}:${occurrenceIndex}`}><span>Occurrence {occurrenceIndex + 1}</span><button onClick={() => openVideoList((occurrence.marker_ids || []).join(","), `${player.display_name} - ${eventType.event_type}`, false, occurrence.marker_ids || [], true, true)}>▶ Play</button><small>{labelOf(occurrence.match?.competition)} · {occurrence.match?.date ? new Date(occurrence.match.date).toLocaleDateString("en-GB") : ""}</small></div>)}</div></td></tr>}
            </React.Fragment>;
          })}
        </tbody></table>
      </div>
    ))}
  </section>
);

// Competitions and players are paginated on the API, so their pickers search the
// API as the user types and fetch the next page when the list is scrolled to its
// end, instead of filtering only the first page locally. Returns MultiSelect props.
const useSearchableOptions = ({ endpoint, params, enabled = true }) => {
  const [query, setQuery] = useState("");
  const search = query.trim();
  const listKey = JSON.stringify({ params, search });
  const [requested, setRequested] = useState({ listKey, page: 1 });
  const [loaded, setLoaded] = useState({ listKey: null, page: 0, options: [], hasMore: false });
  const { listKey: requestedListKey, page: requestedPage } = requested;

  useEffect(() => {
    const timeoutId = setTimeout(
      () => setRequested((current) => (current.listKey === listKey ? current : { listKey, page: 1 })),
      search ? SEARCH_DELAY_MS : 0
    );
    return () => clearTimeout(timeoutId);
  }, [listKey, search]);

  useEffect(() => {
    if (!enabled || requestedListKey !== listKey) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const { params: requestedParams, search: requestedSearch } = JSON.parse(requestedListKey);
    $apiV1.get(endpoint, {
      params: { ...requestedParams, page: requestedPage, ...(requestedSearch ? { search: requestedSearch } : {}) },
      signal: controller.signal,
    })
      .then((result) => {
        if (cancelled) return;
        setLoaded((current) => ({
          listKey: requestedListKey,
          page: requestedPage,
          options: uniqueById(requestedPage === 1 ? unwrap(result) : [...current.options, ...unwrap(result)]),
          hasMore: Boolean(result.data?.next),
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded((current) => ({ listKey: requestedListKey, page: requestedPage, options: requestedPage === 1 ? [] : current.options, hasMore: false }));
      });
    return () => { cancelled = true; controller.abort(); };
  }, [enabled, endpoint, listKey, requestedListKey, requestedPage]);

  const settled = loaded.listKey === listKey && requestedListKey === listKey && loaded.page === requestedPage;
  return {
    options: enabled && loaded.listKey === listKey ? loaded.options : [],
    loading: enabled && !settled,
    onQueryChange: setQuery,
    onLoadMore: () => { if (enabled && settled && loaded.hasMore) setRequested({ listKey, page: loaded.page + 1 }); },
  };
};

const PlayerRecruitmentSequences = forwardRef(function PlayerRecruitmentSequences(
  { onTemplateFiltersChange },
  ref
) {
  const [sequences, setSequences] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [picker, setPicker] = useState(null);
  const [eventQuery, setEventQuery] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [scope, setScope] = useState({ competition: [], season: [], team: [], player: [], matches: [] });
  const competitionPicker = useSearchableOptions({
    endpoint: "/platform/competition",
    params: { page_size: COMPETITION_PAGE_SIZE, sort: "alphabet" },
  });
  // With teams selected the Player list is their rosters; otherwise every player
  // of the competition's season, which is too many to load at once.
  const seasonPlayerPicker = useSearchableOptions({
    endpoint: "/platform/player/",
    params: { competition_id: idOf(scope.competition[0]), year_season_id: seasonIdOf(scope.season[0]) },
    enabled: Boolean(scope.competition.length && scope.season.length && !scope.team.length),
  });
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});

  const templateFilters = useMemo(
    () => buildPlayerRecruitmentTemplateFilters({ scope, sequences }),
    [scope, sequences]
  );

  useEffect(() => {
    onTemplateFiltersChange?.(templateFilters);
  }, [onTemplateFiltersChange, templateFilters]);

  useImperativeHandle(
    ref,
    () => ({
      applyTemplate: (filters) => {
        const parsed = parsePlayerRecruitmentTemplateFilters(filters);
        setScope(parsed.scope);
        setSequences(parsed.sequences);
        setPicker(null);
        setEventQuery("");
        setResponse(null);
        setError("");
        setExpanded({});
      },
    }),
    []
  );

  useEffect(() => {
    $apiV2.get("/stats/marker_actions", { params: { page: 1, page_size: 250, events_only: 1 } })
      .then((eventsResult) => setEventOptions(buildSequenceEventOptionsFromApiMarkers(unwrap(eventsResult))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const competition = scope.competition[0];
    if (!competition) { setSeasons([]); return; }
    $apiV1.get("/platform/year_season", { params: { page: 1, per_page: 100, competitions_id: idOf(competition) } })
      .then((result) => setSeasons(uniqueByLabel(unwrap(result)))).catch(() => setSeasons([]));
  }, [scope.competition]);

  useEffect(() => {
    const competition = scope.competition[0];
    const season = scope.season[0];
    if (!competition || !season) { setTeams([]); return; }
    const seasonId = seasonIdOf(season);
    const platformSeason = (Array.isArray(competition.seasons) ? competition.seasons : []).find((entry) => {
      const entrySeasonId = entry?.year_season?.id ?? entry?.year_season_id ?? entry?.season_id;
      return String(entrySeasonId) === String(seasonId);
    });
    if (!platformSeason?.id) { setTeams([]); return; }
    $apiV1.get(`/platform/season/${platformSeason.id}`)
      .then((result) => setTeams(uniqueByLabel(Array.isArray(result.data?.teams) ? result.data.teams : [])))
      .catch(() => setTeams([]));
  }, [scope.competition, scope.season]);

  useEffect(() => {
    const competition = scope.competition[0];
    const season = scope.season[0];
    if (!competition || !season || !scope.team.length) { setPlayers([]); return; }
    let cancelled = false;
    const loadPlayers = async () => {
      try {
        const rosters = await Promise.all(scope.team.map(async (team) => {
          const teamId = idOf(team);
          const [statisticsResult, playersResult] = await Promise.all([
            $apiV1.get(`/statistics/team_opt/${teamId}`, { params: { year_season_id: seasonIdOf(season) } }),
            $apiV2.get(`/platform/team/${teamId}/players_opt`, { params: { year_season_id: seasonIdOf(season) } }),
          ]);
          const statisticsPlayers = Array.isArray(statisticsResult.data?.players_list) ? statisticsResult.data.players_list : [];
          return statisticsPlayers.length ? statisticsPlayers : unwrap(playersResult).map((player) => ({
            ...player,
            display_name: player.display_name || `${player.name || ""} ${player.surname || ""}`.trim() || "Unknown",
          }));
        }));
        if (!cancelled) setPlayers(uniqueByLabel(rosters.flat()));
      } catch (requestError) {
        if (!cancelled) setPlayers([]);
      }
    };
    loadPlayers();
    return () => { cancelled = true; };
  }, [scope.competition, scope.season, scope.team]);

  useEffect(() => {
    const competition = scope.competition[0];
    const season = scope.season[0];
    const matchTeams = scope.team.length ? scope.team : teams;
    if (!competition || !season || (!scope.player.length && !matchTeams.length)) { setMatches([]); return; }
    let cancelled = false;
    setMatches([]);
    const requests = scope.player.length
      ? scope.player.map((player) => $apiV1.get(`/statistics/player_opt/${idOf(player)}`, { params: { year_season_id: seasonIdOf(season) } }))
      : matchTeams.map((team) => $apiV2.get(`/platform/team/${idOf(team)}/matches_opt?year_season_id=${seasonIdOf(season)}&params_ids=`));
    Promise.all(requests)
      .then((results) => {
        if (cancelled) return;
        const byId = new Map();
        const receivedMatches = scope.player.length
          ? results.flatMap((result) => Array.isArray(result.data?.matches_list) ? result.data.matches_list : [])
          : results.flatMap(unwrap);
        receivedMatches
          .filter((match) => String(competitionIdOf(match)) === String(competitionIdOf(competition)))
          .forEach((match) => byId.set(String(idOf(match)), match));
        setMatches(Array.from(byId.values()));
      }).catch(() => { if (!cancelled) setMatches([]); });
    return () => { cancelled = true; };
  }, [scope.competition, scope.season, scope.team, scope.player, teams]);

  const validSequences = useMemo(() => sequences.filter((sequence) => sequence.included && sequence.steps.length), [sequences]);
  const canSearch = validSequences.length > 0 && scope.competition.length > 0 && scope.season.length > 0;
  const filteredEvents = eventOptions.filter((option) => `${option.label} ${option.group || ""}`.toLowerCase().includes(eventQuery.toLowerCase()));
  const allResultMarkerIds = useMemo(() => {
    const markerIds = new Set();
    (response?.event_types || []).forEach((eventType) => {
      (eventType.players || []).forEach((player) => {
        (player.sequences || []).forEach((occurrence) => {
          (occurrence.marker_ids || []).forEach((markerId) => markerIds.add(String(markerId)));
        });
      });
    });
    return Array.from(markerIds);
  }, [response]);

  const addSequence = () => setSequences((current) => current.length >= MAX_SEQUENCES ? current : [...current.map((sequence) => ({ ...sequence, editing: false })), { id: uid(), name: `Sequence ${current.length + 1}`, steps: [], included: false, editing: true }]);
  const updateSequence = (id, patch) => setSequences((current) => current.map((sequence) => sequence.id === id ? { ...sequence, ...patch } : sequence));
  const loadExamples = () => setSequences(EXAMPLES.map((example, index) => ({
    id: uid(), name: example.name, included: true, editing: index === 0,
    steps: example.labels.map((label) => {
      const option = eventOptions.find((item) => item.label.toLowerCase() === label.toLowerCase()) || eventOptions.find((item) => item.label.toLowerCase().includes(label.toLowerCase()));
      return option ? createSequenceStepFromEventOption(option, uid(), "selected_team") : { id: uid(), sourceLabel: label, invalid: true };
    }),
  })));
  const chooseEvent = (option) => {
    if (!picker) return;
    setSequences((current) => current.map((sequence) => sequence.id !== picker.sequenceId ? sequence : {
      ...sequence,
      steps: picker.index == null
        ? [...sequence.steps, createSequenceStepFromEventOption(option, uid(), "selected_team")]
        : sequence.steps.map((step, index) => index === picker.index ? createSequenceStepFromEventOption(option, step.id, "selected_team") : step),
    }));
    setPicker(null); setEventQuery("");
  };

  const search = async () => {
    if (!canSearch) return;
    setLoading(true); setError("");
    const base = buildSequenceSearchPayload({
      competitionId: idOf(scope.competition[0]), seasonId: seasonIdOf(scope.season[0]),
      teamIds: scope.team.map(idOf), playerIds: scope.player.map(idOf), matchIds: scope.matches.map(idOf), steps: validSequences[0].steps,
    });
    base.sequences = validSequences.map((sequence) => ({ name: sequence.name, steps: buildSequenceSearchPayload({ competitionId: idOf(scope.competition[0]), seasonId: seasonIdOf(scope.season[0]), steps: sequence.steps.filter((step) => !step.invalid) }).steps }));
    try { const result = await $apiV2.post("/stats/player-recruitment-sequences", base); setResponse(result.data || { event_types: [] }); }
    catch (requestError) { setError(requestError?.response?.data?.message || "Something went wrong - please try again."); }
    finally { setLoading(false); }
  };

  return <div className="prs-wrap">
    <div className="prs-pagehead"><h1>Player Sequences</h1><p>Find the players who best match a tactical pattern</p></div>
    <nav className="prs-stepper">
      <div className={`prs-stepitem ${validSequences.length ? "done" : "active"}`}><span>1</span><div><b>Build your sequences</b><small>Up to 5 — the way your club plays</small></div></div>
      <div className={`prs-stepitem ${scope.competition.length && scope.season.length ? "done" : validSequences.length ? "active" : ""}`}><span>2</span><div><b>Set the search scope</b><small>Choose competition and season</small></div></div>
      <div className={`prs-stepitem ${response ? "active" : ""}`}><span>3</span><div><b>Read the players</b><small>Top 5 per event type, with clips</small></div></div>
    </nav>

    <section className={`prs-panel ${validSequences.length ? "done" : "active"}`}>
      <div className="prs-eyebrow"><span>1</span><b>Step 1 of 3</b><small>{sequences.length} of 5 sequences</small></div>
      <h2>Build your Playing Style</h2>
      <p className="prs-sub">Create up to five sequences that describe how your club plays. Name each one, chain its events in order, then tick "Include in search" on the ones you want in this run. They stay saved for the whole club.</p>
      {sequences.length === 0 && <div className="prs-firstrun"><div><b>No sequences yet</b><p>Start with one play you recognise from your own team — a build-up, a press trigger, a wide overload. Add its events in the order they happen. You can save five in total.</p><div className="prs-firstrun-btns"><button className="prs-primary" onClick={addSequence}>+ Create your first sequence</button><button className="prs-ghost" onClick={loadExamples}>Load an example instead</button></div></div><div className="prs-demo"><i>[Long Ball+]</i><em>→</em><i>[Dribbling Made+]</i><em>→</em><i>[Cross+]</i><em>→</em><i>[Shots on Target]</i></div></div>}
      <div className="prs-cardrow">
        {sequences.map((sequence) => <div className={`prs-seqcard ${sequence.editing ? "editing" : ""} ${sequence.included && !sequence.steps.length ? "invalid" : ""}`} key={sequence.id}>
          <div className="prs-seqhead"><input value={sequence.name} onChange={(event) => updateSequence(sequence.id, { name: event.target.value })}/><div><button onClick={() => updateSequence(sequence.id, { editing: !sequence.editing })}>✎</button><button onClick={() => setSequences((current) => current.filter((item) => item.id !== sequence.id))}>×</button></div></div>
          <div className="prs-chiprow">{sequence.steps.map((step, index) => <React.Fragment key={step.id}>{index > 0 && <em>→</em>}<button className="prs-evchip" onClick={() => setPicker({ sequenceId: sequence.id, index })}>{step.sourceLabel}<span onClick={(event) => { event.stopPropagation(); updateSequence(sequence.id, { steps: sequence.steps.filter((item) => item.id !== step.id) }); }}>×</span></button></React.Fragment>)}<button className="prs-add-event" onClick={() => setPicker({ sequenceId: sequence.id, index: null })}>+ Add Event</button></div>
          <label className="prs-include"><input type="checkbox" checked={sequence.included} onChange={(event) => updateSequence(sequence.id, { included: event.target.checked })}/> Include in search</label>
        </div>)}
        <button className="prs-addseq" disabled={sequences.length >= MAX_SEQUENCES} onClick={addSequence}><span>+</span>Add Sequence</button>
      </div>
      <div className={`prs-nextcue ${validSequences.length ? "ready" : ""}`}><span>•</span><b>{validSequences.length ? "Ready:" : "Next:"}</b> {validSequences.length ? `${validSequences.length} sequence${validSequences.length > 1 ? "s" : ""} ticked - set the search scope below.` : "create your first sequence."}</div>
    </section>

    <section className={`prs-panel ${validSequences.length ? "active" : ""}`}>
      <div className="prs-eyebrow"><span>2</span><b>Step 2 of 3</b></div><h2>Search scope</h2><p className="prs-sub">Competition and Season are required. Team, Player and Matches narrow the pool further.</p>
      <div className="prs-grid">
        <MultiSelect label="Competition" required {...competitionPicker} selected={scope.competition} onChange={(value) => setScope({ competition: value, season: [], team: [], player: [], matches: [] })} placeholder="Select competitions" />
        <MultiSelect label="Season" required options={seasons} selected={scope.season} onChange={(value) => setScope((current) => ({ ...current, season: value, team: [], player: [], matches: [] }))} placeholder={scope.competition.length ? "Select seasons" : "Select competition first"} disabled={!scope.competition.length} getId={seasonIdOf}/>
        <MultiSelect label="Team" options={teams} selected={scope.team} onChange={(value) => setScope((current) => ({ ...current, team: value, player: [], matches: [] }))} placeholder={!scope.competition.length ? "Select competition first" : !scope.season.length ? "Select season first" : "All teams"} disabled={!scope.season.length}/>
        <MultiSelect label="Player" {...(scope.team.length ? { options: players } : seasonPlayerPicker)} selected={scope.player} onChange={(value) => setScope((current) => ({ ...current, player: value }))} placeholder={!scope.competition.length ? "Select competition first" : !scope.season.length ? "Select season first" : "All players"} disabled={!scope.season.length}/>
        <MultiSelect label="Matches" options={matches} selected={scope.matches} onChange={(value) => setScope((current) => ({ ...current, matches: value }))} placeholder={!scope.competition.length ? "Select competition first" : !scope.season.length ? "Select season first" : "All matches"} disabled={!scope.season.length} getLabel={(match) => matchLabelOf(match, scope.team)}/>
      </div>
      <div className="prs-filterfoot"><span>{!validSequences.length ? "Create at least one sequence to search with." : !scope.competition.length || !scope.season.length ? "Select a competition and a season to search." : `${validSequences.length} sequences · ${validSequences.reduce((sum, sequence) => sum + sequence.steps.length, 0)} event types`}</span><div><button className="prs-secondary" onClick={() => setScope({ competition: [], season: [], team: [], player: [], matches: [] })}>Reset filters</button><button className="prs-primary" disabled={!canSearch || loading} onClick={search}>{loading ? "Searching..." : "Search players"}</button></div></div>
    </section>

    {(loading || response || error) && <section className="prs-panel prs-results active">
      <div className="prs-eyebrow"><span>3</span><b>Step 3 of 3</b></div><div className="prs-results-head"><h2>Results</h2>{allResultMarkerIds.length > 0 && <button className="prs-play-all" onClick={() => openVideoList(allResultMarkerIds.join(","), "All player recruitment events", false, allResultMarkerIds, true, true)}>▶ View all events <span>{allResultMarkerIds.length}</span></button>}</div>
      {loading ? <div className="prs-state"><b>Searching player sequences…</b></div> : error ? <div className="prs-state"><b>{error}</b></div> : !(response?.event_types || []).length ? <div className="prs-state"><b>No results found for this scope</b>Try widening your competition, season, or team filters.</div> : <RecruitmentSequenceResults eventTypes={response.event_types} expanded={expanded} setExpanded={setExpanded} />}
    </section>}

    {picker && <div className="prs-picker" onMouseDown={(event) => event.target === event.currentTarget && setPicker(null)}><div className="prs-picker-box"><input autoFocus value={eventQuery} onChange={(event) => setEventQuery(event.target.value)} placeholder="Search events..."/><div className="prs-picker-list">{filteredEvents.map((option) => <button key={option.id} onClick={() => chooseEvent(option)}><small>{option.group}</small>{option.label}</button>)}{!filteredEvents.length && <div className="prs-picker-none">No events match "{eventQuery}"</div>}</div></div></div>}
  </div>;
});

export default PlayerRecruitmentSequences;
